import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  FaTooth,
  FaUserMd,
  FaHeart,
  FaShieldAlt,
  FaSmile,
  FaHandsHelping
} from "react-icons/fa";

const trustPoints = [
  {
    icon: <FaUserMd className="text-2xl sm:text-3xl text-teal-500" />,
    title: "Experienced Doctors",
    desc: "Skilled dental professionals with years of clinical expertise."
  },
  {
    icon: <FaTooth className="text-2xl sm:text-3xl text-teal-500" />,
    title: "Modern Technology",
    desc: "Advanced equipment for accurate diagnosis and painless treatments."
  },
  {
    icon: <FaShieldAlt className="text-2xl sm:text-3xl text-teal-500" />,
    title: "Sterilized Clinic",
    desc: "Strict hygiene protocols and clean environment for patient safety."
  },
  {
    icon: <FaSmile className="text-2xl sm:text-3xl text-teal-500" />,
    title: "Affordable Care",
    desc: "Quality dental treatments at transparent and reasonable prices."
  },
  {
    icon: <FaHandsHelping className="text-2xl sm:text-3xl text-teal-500" />,
    title: "Friendly Staff",
    desc: "Caring and supportive team dedicated to patient comfort."
  },
  {
    icon: <FaHeart className="text-2xl sm:text-3xl text-red-500" />,
    title: "Patient First Approach",
    desc: "Personalized care focused on your dental health and satisfaction."
  }
];

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isPaused) return;
    
    intervalRef.current = setInterval(() => {
      setIndex((prev) => (prev + 1) % trustPoints.length);
    }, 3000); // ✅ Changed to 3 seconds

    return () => clearInterval(intervalRef.current);
  }, [isPaused]);

  return (
    <section id="testimonials" className="py-16 sm:py-20 md:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#F8FBFC] to-white relative overflow-hidden">
      
      {/* Animated Background Blobs */}
      <div className="absolute inset-0">
        <motion.div
          animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          className="absolute -bottom-40 -right-40 w-96 h-96 bg-teal-50/40 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ 
            x: [0, 50, 0, -50, 0],
            y: [0, -30, 0, 30, 0],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-teal-100/10 rounded-full blur-3xl"
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto">
        
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-teal-50/80 backdrop-blur-sm px-4 py-2 rounded-full mb-4 border border-teal-100"
          >
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
            <span className="text-teal-600 text-xs sm:text-sm font-semibold tracking-wide">WHY PATIENTS TRUST US</span>
            <span className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></span>
          </motion.div>
          
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1E293B] mb-4"
          >
            Why Patients <span className="bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] bg-clip-text text-transparent">Trust IQRA</span>
          </motion.h2>
          
          <motion.div
            initial={{ opacity: 0, width: 0 }}
            whileInView={{ opacity: 1, width: "5rem" }}
            transition={{ duration: 0.5, delay: 0.2 }}
            viewport={{ once: true }}
            className="h-1 bg-gradient-to-r from-[#2CA6B1] to-[#4FD1C5] mx-auto rounded-full"
          />
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-gray-500 text-sm sm:text-base max-w-2xl mx-auto mt-4"
          >
            We are dedicated to providing exceptional dental care with modern technology and a patient-first approach.
          </motion.p>
        </div>

        {/* Slider Card - Auto slide only, no dots or counter */}
        <div 
          className="relative max-w-3xl mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div className="relative min-h-[320px] sm:min-h-[280px] flex items-center justify-center px-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ 
                  duration: 0.5, 
                  type: "spring",
                  stiffness: 400,
                  damping: 35
                }}
                className="w-full"
              >
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-300 border border-white/50">
                  
                  {/* Icon */}
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 rounded-full bg-teal-50 flex items-center justify-center">
                      {trustPoints[index].icon}
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-xl sm:text-2xl font-bold text-[#1E293B] text-center mb-3">
                    {trustPoints[index].title}
                  </h3>

                  {/* Description */}
                  <motion.p 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.4 }}
                    className="text-gray-600 text-sm sm:text-base leading-relaxed text-center px-2"
                  >
                    {trustPoints[index].desc}
                  </motion.p>

                  {/* Divider */}
                  <div className="flex justify-center mt-4">
                    <div className="w-12 h-px bg-gradient-to-r from-transparent via-teal-400 to-transparent"></div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}